import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CalendarCheck, Loader2, CheckCircle2, Copy, ExternalLink, Lock, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';
import { PAYMENT_METHOD_META } from './bookingStatusMeta';
import { PaymentMethodOption, PaymentMethodMark } from './PaymentMethodBadge';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../../lib/whatsapp';
import { errMsg } from '../finance/shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const BLANK = { court_id: '', date: todayIso(), start_time: '', duration: 1, customer_name: '', customer_phone: '', notes: '', payment_method: '' };
const START_SLOTS_DEFAULT = Array.from({ length: 18 }, (_, i) => `${String(6 + i).padStart(2, '0')}:00`);
const DURATIONS = [1, 1.5, 2, 3];
const WEEKDAY_SHORT = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const addHours = (start, hours) => {
  if (!start) return '';
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + hours * 60;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};
const wouldWrapMidnight = (start, hours) => {
  if (!start) return false;
  const [h, m] = start.split(':').map(Number);
  return h * 60 + m + hours * 60 >= 24 * 60;
};
const remainingWeekDays = () => {
  const today = new Date();
  const dow = today.getDay(); // 0 = أحد
  const days = [];
  for (let i = 1; dow + i <= 6; i++) {
    const d = new Date(today); d.setDate(d.getDate() + i);
    days.push({ date: d.toISOString().slice(0, 10), label: WEEKDAY_SHORT[d.getDay()], dayNum: d.getDate() });
  }
  return days;
};

const copyText = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('تم النسخ'); } catch { toast.error('تعذّر النسخ'); } };
const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

export default function VenueBookingRequestForm({ venue }) {
  const [form, setForm] = useState(BLANK);
  const [dateMode, setDateMode] = useState('today');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [occupied, setOccupied] = useState([]);
  const successRef = useRef(null);

  useEffect(() => {
    if (result && successRef.current) successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [result]);

  useEffect(() => {
    axios.get(`${API}/public/venues/${venue.id}/payment-methods`).then((res) => setPaymentMethods(res.data || [])).catch(() => setPaymentMethods([]));
  }, [venue.id]);

  useEffect(() => {
    if (!form.date) { setOccupied([]); return; }
    axios.get(`${API}/public/venues/${venue.id}/occupied-slots`, { params: { date: form.date, court_id: form.court_id || undefined } })
      .then((res) => setOccupied(res.data || []))
      .catch(() => setOccupied([]));
  }, [venue.id, form.date, form.court_id]);

  const START_SLOTS = venue.operating_hours?.length
    ? venue.operating_hours.slice().sort((a, b) => a - b).map((h) => `${String(h).padStart(2, '0')}:00`)
    : START_SLOTS_DEFAULT;
  const isSlotOccupied = (t) => {
    const tm = timeToMin(t);
    return occupied.some((o) => tm >= timeToMin(o.start_time) && tm < timeToMin(o.end_time));
  };

  const showPaymentStep = paymentMethods.length > 1 || (paymentMethods.length === 1 && paymentMethods[0].method_id !== 'pay_on_site');
  const selectedMethod = paymentMethods.find((m) => m.method_id === form.payment_method);
  const weekDays = useMemo(remainingWeekDays, []);

  const court = (venue.courts || []).find((c) => c.id === form.court_id);
  const hourly = court ? court.price_per_hour : venue.price_per_hour;
  const endTime = addHours(form.start_time, form.duration);
  const total = hourly && form.start_time ? Math.round(hourly * form.duration) : null;
  const canSubmit = form.date && form.start_time && !isSlotOccupied(form.start_time) && form.customer_name.trim().length >= 2 && form.customer_phone.replace(/\D/g, '').length >= 9 && (!showPaymentStep || form.payment_method);

  const pickDate = (mode, date) => { setDateMode(mode); if (date) setForm({ ...form, date }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time) { toast.error('حدد التاريخ ووقت البداية'); return; }
    if (wouldWrapMidnight(form.start_time, form.duration)) { toast.error('هذه المدة تتجاوز منتصف الليل، اختر مدة أقصر'); return; }
    if (!form.customer_name.trim() || form.customer_name.trim().length < 2) { toast.error('يرجى إدخال اسم صحيح'); return; }
    const phoneDigits = form.customer_phone.replace(/\D/g, '');
    if (phoneDigits.length < 9 || phoneDigits.length > 15) { toast.error('يرجى إدخال رقم جوال صحيح'); return; }
    if (showPaymentStep && !form.payment_method) { toast.error('اختر طريقة الدفع'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/venues/${venue.id}/booking-requests`, {
        date: form.date, start_time: form.start_time, end_time: endTime, court_id: form.court_id || null,
        customer_name: form.customer_name, customer_phone: form.customer_phone, notes: form.notes,
        payment_method: form.payment_method || null,
      });
      setResult(res.data);
      toast.success('تم إرسال طلب الحجز بنجاح');
    } catch (err) { toast.error(errMsg(err, 'فشل إرسال الطلب')); } finally { setSubmitting(false); }
  };

  if (result) {
    const trackUrl = `${window.location.origin}/booking-status/${result.tracking_token}`;
    const isPendingPayment = result.status === 'pending_payment';
    const ownerNumber = normalizeWhatsAppNumber(venue.whatsapp_number);
    const ownerWaLink = ownerNumber ? buildWaMeLink(ownerNumber, [
      `طلب حجز جديد — ${venue.name}`,
      court ? `الكورت: ${court.label}` : null,
      `الاسم: ${form.customer_name}`,
      `الجوال: ${form.customer_phone}`,
      `التاريخ: ${form.date}`,
      `الوقت: ${form.start_time} - ${endTime}`,
      total !== null ? `المبلغ: ${total} ر.س` : null,
      selectedMethod ? `طريقة الدفع: ${selectedMethod.label}` : null,
      form.notes ? `ملاحظات: ${form.notes}` : null,
      `رابط تتبع الحجز: ${trackUrl}`,
    ].filter(Boolean).join('\n')) : null;
    return (
      <div className="space-y-4 rounded-xl border border-teal-400/30 bg-teal-500/10 p-6 text-center" ref={successRef} data-testid="booking-request-success">
        <CheckCircle2 className="mx-auto mb-1 h-10 w-10 text-teal-300" />
        <p className="font-bold text-white">{isPendingPayment ? 'تم استلام طلبك — بانتظار الدفع' : 'تم إرسال طلب حجزك بنجاح'}</p>
        <p className="text-sm text-slate-300">
          {isPendingPayment ? 'حوّل المبلغ عبر الطريقة المختارة، ثم افتح الرابط أدناه لرفع إثبات الدفع.' : 'طلبك الآن قيد المراجعة، وسيتم التواصل معك لتأكيده.'}
        </p>

        {isPendingPayment && selectedMethod?.details && (
          <div className="rounded-lg bg-white/5 p-3 text-right text-sm text-slate-200" data-testid="booking-success-payment-details">
            <p className="mb-1.5 flex items-center justify-end gap-1.5 text-xs font-bold text-orange-300">
              بيانات {selectedMethod.label}
              {PAYMENT_METHOD_META[selectedMethod.method_id] && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white p-0.5 shadow-sm">
                  <PaymentMethodMark meta={PAYMENT_METHOD_META[selectedMethod.method_id]} className="h-3.5 w-3.5" />
                </span>
              )}
            </p>
            <div className="flex items-center justify-between gap-2">
              <p className="whitespace-pre-line text-xs">{selectedMethod.details}</p>
              <button type="button" onClick={() => copyText(selectedMethod.details)} data-testid="booking-success-copy-details" className="shrink-0 rounded-md bg-white/10 p-1.5 hover:bg-white/20"><Copy className="h-3.5 w-3.5 text-slate-200" /></button>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-dashed border-teal-400/40 bg-[#0A192F] p-3" data-testid="booking-success-tracking-link">
          <p className="mb-1.5 text-xs font-bold text-teal-300">احفظ رابط تتبع حجزك — لرفع الإثبات ومتابعة الحالة لاحقاً</p>
          <div className="flex items-center gap-2">
            <p dir="ltr" className="flex-1 truncate rounded-md bg-white/5 px-2 py-1.5 text-xs text-slate-300">{trackUrl}</p>
            <button type="button" onClick={() => copyText(trackUrl)} data-testid="booking-success-copy-link" className="rounded-md bg-white/10 p-1.5 hover:bg-white/20"><Copy className="h-3.5 w-3.5 text-slate-200" /></button>
            <a href={trackUrl} data-testid="booking-success-open-link" className="rounded-md bg-white/10 p-1.5 hover:bg-white/20"><ExternalLink className="h-3.5 w-3.5 text-slate-200" /></a>
          </div>
        </div>

        {ownerWaLink && (
          <a
            href={ownerWaLink} target="_blank" rel="noreferrer" data-testid="booking-success-notify-owner-whatsapp"
            className="flex items-center justify-center gap-2 rounded-lg border border-[#25D366]/30 bg-[#25D366]/10 py-2.5 text-xs font-bold text-[#25D366] transition-colors hover:bg-[#25D366]/20"
          >
            <MessageCircle className="h-4 w-4" /> إرسال تفاصيل الحجز لصاحب الملعب عبر واتساب
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="booking-request-form">
      <div className="space-y-4 rounded-xl border border-white/10 p-4 pb-24">
        {venue.courts?.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-semibold text-slate-400">اختر المقاس/الكورت</p>
            <div className="flex flex-wrap gap-2" data-testid="booking-request-courts">
              {venue.courts.map((c) => (
                <button
                  type="button" key={c.id} onClick={() => setForm({ ...form, court_id: c.id })} data-testid={`booking-request-court-${c.id}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${form.court_id === c.id ? 'bg-teal-400 text-[#0A192F] shadow-[0_0_0_3px_rgba(45,212,191,0.25)]' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}
                >
                  {c.label} {c.price_per_hour ? `— ${c.price_per_hour} ر.س/س` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="mb-1.5 text-xs font-semibold text-slate-400">التاريخ</p>
          <div className="flex flex-wrap gap-1.5" data-testid="booking-request-date-quick">
            <button type="button" onClick={() => pickDate('today', addDays(0))} data-testid="booking-request-date-today" className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${dateMode === 'today' ? 'bg-teal-400 text-[#0A192F]' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}>اليوم</button>
            <button type="button" onClick={() => pickDate('tomorrow', addDays(1))} data-testid="booking-request-date-tomorrow" className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${dateMode === 'tomorrow' ? 'bg-teal-400 text-[#0A192F]' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}>غدًا</button>
            <button type="button" onClick={() => pickDate('week', null)} data-testid="booking-request-date-week" className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${dateMode === 'week' ? 'bg-teal-400 text-[#0A192F]' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}>هذا الأسبوع</button>
            <button type="button" onClick={() => pickDate('custom', null)} data-testid="booking-request-date-custom" className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${dateMode === 'custom' ? 'bg-teal-400 text-[#0A192F]' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}>تحديد تاريخ</button>
          </div>
          {dateMode === 'week' && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1 no-scrollbar" data-testid="booking-request-week-strip">
              {weekDays.length === 0 ? (
                <p className="text-xs text-slate-500">لا توجد أيام متبقية هذا الأسبوع، جرّب «تحديد تاريخ»</p>
              ) : weekDays.map((w) => (
                <button
                  type="button" key={w.date} onClick={() => setForm({ ...form, date: w.date })} data-testid={`booking-request-weekday-${w.date}`}
                  className={`flex shrink-0 flex-col items-center rounded-xl px-3 py-2 text-xs font-bold transition-all ${form.date === w.date ? 'bg-teal-400 text-[#0A192F]' : 'border border-white/10 text-slate-300 hover:border-teal-400/50'}`}
                >
                  <span>{w.label}</span><span className="text-[10px] opacity-80">{w.dayNum}</span>
                </button>
              ))}
            </div>
          )}
          {dateMode === 'custom' && (
            <div className="mt-2"><PremiumDatePicker value={form.date} onChange={(d) => setForm({ ...form, date: d })} testId="booking-request-date" minDate={todayIso()} inline /></div>
          )}
          {dateMode !== 'custom' && dateMode !== 'week' && (
            <p dir="ltr" className="mt-1.5 text-xs text-slate-400">{form.date}</p>
          )}
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-400">وقت البداية</p>
            {occupied.length > 0 && <p className="flex items-center gap-1 text-[11px] text-orange-300"><span className="h-1.5 w-1.5 rounded-full bg-orange-400" /> محجوز</p>}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="booking-request-start-slots">
            {START_SLOTS.map((t) => {
              const booked = isSlotOccupied(t);
              return (
                <button
                  type="button" key={t} disabled={booked}
                  onClick={() => setForm({ ...form, start_time: t, duration: wouldWrapMidnight(t, form.duration) ? 1 : form.duration })}
                  data-testid={`booking-request-slot-${t}`}
                  className={`relative rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${booked ? 'cursor-not-allowed border border-orange-400/20 text-slate-600 line-through' : form.start_time === t ? 'bg-teal-400 text-[#0A192F] shadow-[0_0_0_3px_rgba(45,212,191,0.25)]' : 'border border-white/10 text-slate-300 hover:border-teal-400/50'}`}
                >
                  {booked && <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-orange-400" />}
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold text-slate-400">المدة</p>
          <div className="flex flex-wrap gap-1.5" data-testid="booking-request-durations">
            {DURATIONS.map((d) => {
              const disabled = wouldWrapMidnight(form.start_time, d);
              return (
                <button
                  type="button" key={d} disabled={disabled} onClick={() => setForm({ ...form, duration: d })} data-testid={`booking-request-duration-${d}`}
                  className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${disabled ? 'cursor-not-allowed border border-white/5 text-slate-600' : form.duration === d ? 'bg-teal-400 text-[#0A192F] shadow-[0_0_0_3px_rgba(45,212,191,0.25)]' : 'border border-white/10 text-slate-300 hover:border-teal-400/50'}`}
                >
                  {disabled && <Lock className="h-3 w-3" />}
                  {d} ساعة{d > 1 ? (d === 1.5 ? ' ونصف' : 'ات') : ''}
                </button>
              );
            })}
          </div>
        </div>

        {showPaymentStep && (
          <div>
            <p className="mb-1.5 text-xs font-semibold text-slate-400">طريقة الدفع</p>
            <div className="grid grid-cols-2 gap-2" data-testid="booking-request-payment-methods">
              {paymentMethods.map((m) => (
                <PaymentMethodOption
                  key={m.method_id} methodId={m.method_id} selected={form.payment_method === m.method_id}
                  onClick={() => setForm({ ...form, payment_method: m.method_id })}
                  testId={`booking-request-payment-${m.method_id}`}
                />
              ))}
            </div>
            {selectedMethod?.details && (
              <div className="mt-2 rounded-lg bg-white/5 p-3 text-right" data-testid="booking-request-payment-details">
                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    {PAYMENT_METHOD_META[selectedMethod.method_id] && (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm">
                        <PaymentMethodMark meta={PAYMENT_METHOD_META[selectedMethod.method_id]} className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <p className="whitespace-pre-line text-xs text-slate-200">{selectedMethod.details}</p>
                  </span>
                  <button type="button" onClick={() => copyText(selectedMethod.details)} data-testid="booking-request-copy-payment-details" className="shrink-0 rounded-md bg-white/10 p-1.5 hover:bg-white/20"><Copy className="h-3.5 w-3.5 text-slate-200" /></button>
                </div>
                {selectedMethod.proof_required && <p className="mt-1.5 text-[11px] text-orange-300">بعد التحويل ستحتاج رفع إثبات الدفع من رابط تتبع الحجز</p>}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <input placeholder="اسمك" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="booking-request-name" className="rounded-lg border border-white/10 bg-[#0F1829] px-3 py-2 text-sm text-white placeholder:text-slate-500" />
          <input placeholder="رقم الجوال" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="booking-request-phone" className="rounded-lg border border-white/10 bg-[#0F1829] px-3 py-2 text-sm text-white placeholder:text-slate-500" />
        </div>
        <textarea placeholder="ملاحظات (اختياري)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-request-notes" className="w-full rounded-lg border border-white/10 bg-[#0F1829] px-3 py-2 text-sm text-white placeholder:text-slate-500" />
      </div>

      <div className="sticky bottom-0 -mx-4 space-y-2 border-t border-white/10 bg-[#0A192F]/97 px-4 pb-1 pt-3 backdrop-blur-md" data-testid="booking-request-summary">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{venue.name}{court ? ` — ${court.label}` : ''}</span>
          <span dir="ltr">{form.date} · {form.start_time || '--:--'}–{endTime || '--:--'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{hourly ? `${hourly} ر.س/ساعة × ${form.duration}` : 'حدد المقاس/الوقت لعرض السعر'}</span>
          <span className="text-lg font-black text-teal-300">{total !== null ? `${total} ر.س` : '—'}</span>
        </div>
        <button type="submit" disabled={submitting || !canSubmit} data-testid="booking-request-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#F54C07] py-3 text-sm font-bold text-white transition-all hover:bg-[#FF6B2B] disabled:cursor-not-allowed disabled:opacity-40">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
          {submitting ? 'جاري الإرسال...' : 'تأكيد الحجز'}
        </button>
        <p className="pb-2 text-center text-[10px] text-slate-500">بلا حاجة لتسجيل أو حساب — طلبك يُراجَع خلال دقائق</p>
      </div>
    </form>
  );
}
